import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import Topbar from '../../../PulseSurvey/Topbar';
import { StatCard, STAT_ICONS } from '../../../../component/common/designSystem';
import SelectMoreCard from '../../../../component/B2BDashboard/SelectMoreCard';
import ActionCard from '../../../../component/B2BDashboard/ActionCard';
import PlaneHeader from '../../../../component/B2BDashboard/PlaneHeader';
import EngagementBand from '../../../../component/B2BDashboard/EngagementBand';
import PlaneAnalyticsBand from '../../../../component/B2BDashboard/PlaneAnalyticsBand';
import DownloadReportButton from '../../../../component/B2BDashboard/DownloadReportButton';
import DashboardFilterModal, {
  DATE_RANGES,
  EMPTY_FILTERS,
  countActiveFilters
} from '../../../../component/B2BDashboard/DashboardFilterModal';
import {
  PLANE,
  PLANE_META,
  getFeaturesByPlane,
  getFeaturedByPlane,
  getMoreByPlane,
  resolveManageRoute
} from '../../../../utils/dashboardFeatures';
import { fetchDashboard, countNeedingSetup } from '../../../../api/b2bDashboardApi';
import { REPORT_SCOPE } from '../../../../api/companyReportApi';
import { CompanyApi } from '../../../../api/companyApi';
import { getLocalStorageItem } from '../../../../utils/helper';

/**
 * StatCard renders a plain value, so formatting and the loading/empty case are
 * resolved here: "…" while in flight, an em dash when the API had nothing.
 */
const statValue = (value, loading) => {
  if (loading) return '…';
  return Number.isFinite(value) ? Number(value).toLocaleString() : '—';
};

/**
 * Rebuilt B2B dashboard — three planes.
 *
 * Renders for both audiences from one component, exactly as the old dashboard
 * did: the company admin at /:slug/dashboard, and the super admin inside the
 * View Company tab (which passes `companyId`).
 *
 * The filter panel in the Topbar is the single global control for the whole
 * page — there are deliberately no per-card date dropdowns.
 */
function ThreePlaneDashboard({ companyId }) {
  const navigate = useNavigate();

  const userData = useMemo(() => {
    const raw = getLocalStorageItem('userData');
    return raw ? JSON.parse(raw) : null;
  }, []);

  // Embedded = rendered inside the super admin's View Company tab.
  const isEmbedded = Boolean(companyId);
  const resolvedCompanyId = companyId || userData?.companyId;
  const slug = userData?.slug;
  const isCompanyAdmin = userData?.userType === 3 || userData?.userType === 4;

  /* ---------- global filter state (drives the filter dialog) ---------- */
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [filterData, setFilterData] = useState({
    countries: [],
    departments: [],
    ethnicities: [],
    loaded: false
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  /* ---------- dashboard data ---------- */
  const [people, setPeople] = useState(null);
  const [featureStats, setFeatureStats] = useState({});
  const [loading, setLoading] = useState(true);

  /**
   * The department/country/ethnicity lists only exist to populate the filter
   * dialog, so they are fetched the first time it opens rather than on mount —
   * one fewer request in the way of the dashboard rendering.
   */
  useEffect(() => {
    if (!resolvedCompanyId || !isFilterOpen || filterData.loaded) return;
    CompanyApi.getFilterData(resolvedCompanyId)
      .then((res) => {
        // Topbar does `filterData?.countries.map(...)` — optional on the object
        // but not the array — so every key must always be an array.
        const data = res?.data?.data || res?.data || {};
        setFilterData({
          countries: Array.isArray(data.countries) ? data.countries : [],
          departments: Array.isArray(data.departments) ? data.departments : [],
          ethnicities: Array.isArray(data.ethnicities) ? data.ethnicities : [],
          loaded: true
        });
      })
      .catch(() => {
        /* filters simply stay empty — not worth a toast on the dashboard */
      });
  }, [resolvedCompanyId, isFilterOpen, filterData.loaded]);

  useEffect(() => {
    if (!resolvedCompanyId) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    fetchDashboard(resolvedCompanyId, { embedded: isEmbedded })
      .then(({ people: peopleStats, featureStats: stats }) => {
        if (cancelled) return;
        setPeople(peopleStats);
        setFeatureStats(stats);
      })
      .catch((error) => {
        console.error('Failed to load dashboard', error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [resolvedCompanyId, isEmbedded, filters]);

  /**
   * PHASE ONE: a card routes to the feature's existing management page so
   * nothing dead-ends. PHASE TWO swaps this for the feature detail template
   * at /:slug/dashboard/f/:featureKey.
   *
   * Navigation is suppressed in the embedded super-admin view because the
   * management pages are scoped to the logged-in company's slug, not the
   * company being inspected.
   */
  const handleSelectFeature = useCallback(
    (feature) => {
      if (isEmbedded) return;
      const route = resolveManageRoute(feature, slug);
      if (route) navigate(route);
    },
    [isEmbedded, slug, navigate]
  );

  const rangeLabel = DATE_RANGES.find((range) => range.days === filters.rangeDays)?.label || '';
  const activeFilterCount = countActiveFilters(filters);

  const renderPlane = (plane) => {
    const meta = PLANE_META[plane];
    const features = getFeaturesByPlane(plane);
    const featured = getFeaturedByPlane(plane);
    const more = getMoreByPlane(plane);
    const needsSetup = countNeedingSetup(featureStats, plane);

    return (
      <section key={plane}>
        <PlaneHeader
          label={meta.label}
          note={meta.note}
          accent={meta.accent}
          aside={needsSetup > 0 ? `${needsSetup} needs setup` : null}
          action={
            <DownloadReportButton
              scope={REPORT_SCOPE.PLANE}
              plane={plane}
              planeLabel={meta.label}
              filters={filters}
            />
          }
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {featured.map((feature) => (
            <button
              key={feature.key}
              type="button"
              onClick={() => handleSelectFeature(feature)}
              className="text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-shoorah-active rounded-input"
            >
              <StatCard
                icon={feature.icon}
                title={feature.name}
                value={statValue(featureStats[feature.key]?.value, loading)}
              />
            </button>
          ))}

          {more.length > 0 && (
            <SelectMoreCard
              features={features}
              count={more.length}
              onSelect={handleSelectFeature}
              title={meta.label}
              note={meta.note}
            />
          )}
        </div>

        {/* charts for this plane — content defined in utils/planeChartConfig.js */}
        <PlaneAnalyticsBand
          plane={plane}
          features={features}
          stats={featureStats}
          accent={meta.accent}
          loading={loading}
        />
      </section>
    );
  };

  return (
    <div className="relative lg:mb-[45px]">
      {!isEmbedded && (
        <>
          <Helmet>
            <meta charSet="utf-8" />
            <title>Dashboard | Shoorah</title>
          </Helmet>
          {isCompanyAdmin && (
            <Topbar
              title={userData?.name ? `Hi ${userData.name}` : 'Dashboard'}
              isFilterOpen={isFilterOpen}
              setIsFilterOpen={setIsFilterOpen}
              useCustomFilter
            />
          )}
        </>
      )}

      <div className="lg:mt-4 mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
        <h1 className="text-lg md:text-xl font-medium text-shoorah-lightBlack dark:text-shoorah-darkBg">
          {isEmbedded ? 'Company dashboard' : 'Engagement and feature adoption'}
        </h1>
        <p className="text-xs text-shoorah-gray3">
          {rangeLabel ? `Showing ${rangeLabel.toLowerCase()}` : 'Across your people'}
          {filters.department ? ` · ${filters.department}` : ''}
          {activeFilterCount > 0
            ? ` · ${activeFilterCount} filter${activeFilterCount === 1 ? '' : 's'} applied`
            : ''}
        </p>
        </div>

        {/* Everything, in one PDF. The per-section buttons sit on the band
            headings below; this is the "and all of it" case. */}
        {!isEmbedded && (
          <div className="shrink-0">
            <DownloadReportButton
              scope={REPORT_SCOPE.ALL}
              planeLabel="whole dashboard"
              filters={filters}
              variant="button"
              label="Download report"
            />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-6">
        {/* ---------- People ---------- */}
        <section>
          <PlaneHeader
            label={PLANE_META[PLANE.PEOPLE].label}
            note={PLANE_META[PLANE.PEOPLE].note}
            accent={PLANE_META[PLANE.PEOPLE].accent}
            action={
              <DownloadReportButton
                scope={REPORT_SCOPE.PLANE}
                plane={PLANE.PEOPLE}
                planeLabel={PLANE_META[PLANE.PEOPLE].label}
                filters={filters}
              />
            }
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            <StatCard
              icon={STAT_ICONS.users}
              title="Total users"
              value={statValue(people?.totalUsers, loading)}
            />
            <StatCard
              icon={STAT_ICONS.seats}
              title="Empty seats"
              value={statValue(people?.emptySeats, loading)}
            />
            <StatCard
              icon={STAT_ICONS.time}
              title="Active today"
              value={statValue(people?.activeToday, loading)}
            />
            <ActionCard
              icon={STAT_ICONS.more}
              title="View users"
              label="See all"
              onClick={() => !isEmbedded && slug && navigate(`/${slug}/users`)}
            />
          </div>

          {/* charts derived from the same People payload */}
          <div className="mt-3">
            <EngagementBand people={people} loading={loading} />
          </div>
        </section>

        {/* ---------- Feature planes ---------- */}
        {renderPlane(PLANE.SHOORAH)}
        {renderPlane(PLANE.COMPANY)}
      </div>

      <DashboardFilterModal
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        filters={filters}
        onApply={setFilters}
        filterData={filterData}
      />
    </div>
  );
}

ThreePlaneDashboard.propTypes = {
  companyId: PropTypes.string
};

export default ThreePlaneDashboard;
